import { NextApiRequest } from 'next'
import { PaymentMethod } from './ChargeIntent'
import { Order } from './Orders'
import { Frequency } from '@classy/campaign-page-blocks'

export type Transaction = {
  order: Order
  acknowledgements_count: number
  adjustment_amount: number
  applied_fot_percent: number | null
  billing_address1: string
  billing_address2: string
  billing_city: string
  billing_country: string
  billing_first_name: string
  billing_last_name: string
  billing_postal_code: string
  billing_state: string
  browser_info: string
  campaign_id: number
  card_expiration: string
  card_last_four: number
  card_type: string
  charged_at: string
  charged_classy_fees_amount: number
  charged_currency_code: string
  charged_fees_amount: number
  charged_pp_fees_amount: number
  charged_total_gross_amount: number
  classy_fees_amount: number
  comment: string
  company_name: string
  context: {
    [key: string]: unknown
  }
  created_at: string
  currency_code: string
  designation_id: number
  donation_gross_amount: number
  donation_match_id: number
  donation_net_amount: number
  fees_amount: number
  fee_on_top: boolean
  is_donor_covered_fee: boolean
  refunded_at: string | null
  raw_initial_gross_amount: number
  initial_gross_amount: number
  frequency: Frequency
  fundraising_page_id: number
  fundraising_team_id: number
  hide_amount: boolean
  id: string
  in_honor_of: string
  is_anonymous: boolean
  is_gift_aid: boolean
  is_reprocess: boolean
  member_country: string
  member_email_address: string
  member_id: number
  member_name: string
  member_phone: string
  organization_id: number
  overhead_net_amount: number
  parent_transaction_id: number
  payment_gateway:
    | 'StayClassy PayPal'
    | 'Authorize.net'
    | 'PayPal'
    | 'NetworkForGood'
    | 'WePay'
    | 'PayPalExternal'
    | 'BrainTree'
    | 'Classy Pay'
    | 'Dwolla'
    | 'Stripe'
    | 'PayPalCommerce'
    | 'Coinbase'
    | 'Double the Donation Pay'
    | 'Facebook Pay'
  payment_method: string
  payment_type: 'Credit Card' | 'ACH' | 'PayPal' | 'Venmo' | 'Crypto'
  payment_source:
    | 'APPLE_PAY'
    | 'GOOGLE_PAY'
    | 'MANUAL'
    | 'PAYPAL'
    | 'PLAID'
    | 'WALLET'
    | 'VENMO'
    | 'COINBASE'
  pp_fees_amount: string
  pp_reference_id: string
  pp_transaction_id: string
  pp_response: string
  processor_decline_code: string
  promo_code_code: string
  purchased_at: string
  raw_currency_code: string
  raw_donation_gross_amount: number
  raw_overhead_net_amount: number
  raw_total_gross_amount: number
  response_code: string
  response_message: string
  recurring_donation_plan_id?: string
  status: string
  subscription_id: string | null
  supporter_id: string
  success: boolean
  total_gross_amount: number
  tribute_type: string
  updated_at: string
  resource_urn?: string
  queries_log?: {
    [key: string]: unknown
  }
  mongo_queries_log?: {
    [key: string]: unknown
  }
}

export type TransactionItem = {
  product_name: string
  quantity: number
  price: number
  raw_final_price: number
  type: TransactionItemTypeAPIv2
}

export enum TransactionItemTypeAPIv2 {
  DONATION = 'donation',
  OFFLINE_DONATION = 'offline_donation',
  REGISTRATION = 'registration',
  CUSTOM_PRODUCT = 'custom_product', // Deprecated. The API does not create such items anymore.
}

export enum PaymentGateway {
  WEPAY = 'WePay',
  AUTHORIZENET = 'Authorize.net',
  BRAINTREE = 'BrainTree',
  PAYPAL = 'PayPal',
  DWOLLA = 'Dwolla',
  BITPAY = 'Bitpay',
  STRIPE = 'Stripe',
  PAYPALWPPSTAYCLASSY = 'StayClassy PayPal',
  PAYPALWPP = 'PayPal',
  PAYPAL_COMMERCE = 'PayPalCommerce',
  COINBASE = 'Coinbase',
  CLASSYPAY = 'Classy Pay',
}

export enum Apiv2PaymentType {
  CC = 'Credit Card',
  ACH = 'ACH',
  PAYPAL = 'PayPal',
  VENMO = 'Venmo',
  CRYPTO = 'Crypto',
}

export enum PaymentSource {
  APPLEPAY = 'APPLE_PAY',
  GPAY = 'GOOGLE_PAY',
  MANUAL = 'MANUAL',
  PAYPAL = 'PAYPAL',
  VENMO = 'VENMO',
  PLAID = 'PLAID',
  COINBASE = 'COINBASE',
  WALLET = 'WALLET',
}

type SourceCode = {
  c_src?: string
  c_src2?: string
}

export type TransactionRequestBody = {
  billing_address1?: string
  billing_address2?: string
  billing_city?: string
  billing_country?: string
  billing_first_name?: string
  billing_last_name?: string
  billing_postal_code?: string
  billing_state?: string
  browser_info?: string
  comment?: string
  company_name?: string
  c_src?: SourceCode[]
  hide_amount?: boolean
  is_anonymous?: boolean
  is_gift_aid?: boolean // Can only be set if the organization allows it
  is_reprocess?: boolean
  member_id?: string
  member_country?: string
  member_email_address?: string
  member_name?: string
  member_phone?: string
  metadata?: string
  raw_currency_code?: string
  designation_id?: number
  items?: Array<TransactionItem>
  payment_type?: Apiv2PaymentType
  payment_method?: PaymentMethod | 'Classy Pay'
  payment_gateway?: PaymentGateway
  payment_source?: PaymentSource
  status?:
    | 'test'
    | 'canceled'
    | 'incomplete'
    | 'success'
    | 'refunded'
    | 'cb_lost'
    | 'cb_initiated'
    | 'voided'
    | 'pending'
    | 'failed'
  answers?: { question_id: number | string; answer: string }[]
  applied_application_fee_percent?: number | null
  applied_raw_processor_fee_flat?: number | null
  applied_processor_fee_percent?: number | null
  applied_flex_rate_percent?: number | null
  raw_application_fee_amount?: number | null
  raw_processor_fee_amount?: number | null
  raw_flex_rate_amount?: number | null
  raw_initial_gross_amount?: number | null
  employer_match?: {
    employer_id: string | null
    employer_name: string
    employer_search_text: string
  }
}

export type CreateTransactionRequest = NextApiRequest & {
  body: TransactionRequestBody
}

export interface CreateTransactionResponse {
  data: Transaction
  token: string
}

export type UpdateTransactionRequest = NextApiRequest & {
  body: TransactionRequestBody
}

export type UpdateTransactionAnswersRequest = NextApiRequest & {
  body: Required<Pick<TransactionRequestBody, 'answers'>>
}

export type UpdateTransactionCommentRequest = NextApiRequest & {
  body: Required<Pick<TransactionRequestBody, 'comment'>>
}

export type UpdateTransactionEmployerMatchRequest = NextApiRequest & {
  body: Required<Pick<TransactionRequestBody, 'employer_match'>>
}

export type UpdateTransactionDesignationRequest = NextApiRequest & {
  body: Required<Pick<TransactionRequestBody, 'designation_id'>>
}

export interface UpdateTransactionResponse {
  id: string
}

export interface TransactionResponse {
  data: Transaction
  token: string
}
